import React, { useState, useEffect } from "react";
import SelectorHeader from "./components/SelectorHeader";
import DocenteCard from "./components/DocenteCard";
import VisitasSection from "./components/VisitasSection";
import GraficosSection from "./components/GraficosSection";
import Autovaloracion from "./components/Autovaloracion";
import Equipo from "./components/Equipo";
import ObservacionesSection from "./components/ObservacionesSection";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useParams, useNavigate } from "react-router-dom";

import instituciones from "./instituciones.json";

import { loadMomentData, hasData } from "./data/dataLoader";
import { dataConfig } from "./data/dataConfig";
import Fallback from "./Fallback";
import { etapasDocenteOrdenadas } from "./utils/etapasReporte";

const ReporteDocente = ({ modo }) => {
  const [year, setYear] = useState(dataConfig.defaultYear);
  const [momento, setMomento] = useState(dataConfig.defaultMomento);
  const [selectedColegio, setSelectedColegio] = useState("");
  const [selectedDocente, setSelectedDocente] = useState("");

  const { hash } = useParams();
  const modoInstitucion = Boolean(hash);

  const navigate = useNavigate();

  const [data, setData] = useState({
    docenteData: [],
    interaccionData: [],
    visitas: [],
    progresoData: [],
    autovaloracion: { questions: [], answers: [] },
  });

  /** Etapas activas en la barra del docente (toggle); solo aplica con multi-etapa. */
  const [etapasActivas, setEtapasActivas] = useState(() => new Set());

  useEffect(() => {
    if (!modoInstitucion) return;
  
    const found = instituciones.find((i) => i.hash === hash);
    if (found) {
      // Usuarios externos (enlace del correo): mostrar año/momento actual por defecto (ej. 2025-2026 momento 1)
      setYear(dataConfig.defaultYear);
      setMomento(dataConfig.defaultMomento);
      setSelectedColegio(found.nombre);
    } else {
      navigate("/");
    }
  }, [modoInstitucion, hash, navigate]);

  useEffect(() => {
  if (!modoInstitucion || !selectedColegio) return;

  const found = instituciones.find((i) => i.nombre === selectedColegio);
  if (found && found.hash !== hash) {
    navigate(`/${found.hash}`, { replace: true });
  }
}, [modoInstitucion, selectedColegio, hash, navigate]);

  /* =========================
     Carga de datos multianual
     ========================= */
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const loadedData = await loadMomentData(year, momento);

        if (!isMounted) return;

        if (!loadedData || !hasData(loadedData)) {
          setData(null);
        } else {
          setData(loadedData);
        }
      } catch (err) {
        console.error(
          `Error loading data for ${year} - Momento ${momento}:`,
          err
        );
        if (isMounted) setData(null);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [year, momento]);

  const colegios = data?.docenteData?.map((c) => c.nombre) || [];

  const institucionSeleccionada = instituciones.find(
    (i) => i.nombre === selectedColegio
  );

/**
 * TODOS los docentes (para búsqueda global)
 * Se les añade el nombre del colegio
 */
const allDocentes =
  data?.docenteData?.flatMap((colegio) =>
    (colegio.docentes || []).map((docente) => ({
      ...docente,
      colegio: colegio.nombre,
    }))
  ) || [];

/**
 * Docentes visibles según colegio seleccionado
 * Si no hay colegio → lista vacía para el flujo tradicional
 */
const docentesFiltrados = selectedColegio
  ? allDocentes.filter((d) => d.colegio === selectedColegio)
  : [];

/**
 * Docente seleccionado (buscar siempre en ALL)
 */
const docenteSeleccionado = allDocentes.find(
  (d) => d.profesor === selectedDocente
);

  useEffect(() => {
    if (!data?.docenteData || !selectedDocente) {
      setEtapasActivas(new Set());
      return;
    }
    const flat = data.docenteData.flatMap((colegio) =>
      (colegio.docentes || []).map((docente) => ({
        ...docente,
        colegio: colegio.nombre,
      }))
    );
    const d = flat.find((x) => x.profesor === selectedDocente);
    if (!d) {
      setEtapasActivas(new Set());
      return;
    }
    setEtapasActivas(new Set(etapasDocenteOrdenadas(d.etapas)));
  }, [selectedDocente, data]);

  const handleToggleEtapa = (etapa) => {
    setEtapasActivas((prev) => {
      const next = new Set(prev);
      if (next.has(etapa)) {
        if (next.size <= 1) return next;
        next.delete(etapa);
      } else {
        next.add(etapa);
      }
      return next;
    });
  };

  const handleDownloadDocenteReport = async (
    docenteSeleccionado,
    selectedColegio
  ) => {
    if (!selectedColegio || !docenteSeleccionado) {
      alert("Por favor selecciona un colegio y un docente.");
      return;
    }

    const fileName = `Reporte_${docenteSeleccionado.profesor.replaceAll(
      " ",
      "_"
    )}_-_${selectedColegio.replaceAll(" ", "_")}.pdf`;

    try {
      const element = document.querySelector(".reporte-docente");
      const cintillo = document.querySelector(".cintillo");

      if (cintillo) cintillo.style.display = "none";

      const rect = element.getBoundingClientRect();
      const canvas = await html2canvas(element, {
        scale: 2,
        width: rect.width,
        height: rect.height,
        useCORS: true,
      });

      if (cintillo) cintillo.style.display = "";

      const imgData = canvas.toDataURL("image/jpeg", 0.8);
      const pdf = new jsPDF("p", "mm", "a4");

      const marginTop = 10;
      const marginLeft = 10;
      const marginRight = 10;
      const marginBottom = 10;

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const usableWidth = pageWidth - marginLeft - marginRight;
      const usableHeight = pageHeight - marginTop - marginBottom;

      let imgWidth = usableWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > usableHeight) {
        imgHeight = usableHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      const xPosition = (pageWidth - imgWidth) / 2;
      const yPosition = marginTop;

      pdf.addImage(imgData, "JPEG", xPosition, yPosition, imgWidth, imgHeight);
      pdf.save(fileName);
    } catch (error) {
      console.error("Error al generar el PDF:", error);
    }
  };
  
      if (modoInstitucion && !selectedColegio) {
        return <Fallback />;
      }

  return (

    <div className="reporte-docente">
      <SelectorHeader
  colegios={colegios}
  selectedColegio={selectedColegio}
  setSelectedColegio={setSelectedColegio}
  docentes={docentesFiltrados}     // flujo clásico
  allDocentes={allDocentes}        // ✅ búsqueda global
  selectedDocente={selectedDocente}
  setSelectedDocente={setSelectedDocente}
  year={year}
  setYear={setYear}
  momento={momento}
  setMomento={setMomento}
  docenteSeleccionado={docenteSeleccionado}
  handleDownloadDocenteReport={handleDownloadDocenteReport}
  disableDownload={!docenteSeleccionado || data === null}
  ocultarSelectorColegio={modoInstitucion}
/>

      {data === null ? (
        <p style={{ textAlign: "center", marginTop: "2rem" }}>
          No hay información disponible para el momento pedagógico seleccionado.
        </p>
      ) : docenteSeleccionado ? (
        <>
          <DocenteCard
            docente={docenteSeleccionado}
            colegio={selectedColegio}
            etapasActivas={etapasActivas}
            onToggleEtapa={handleToggleEtapa}
          />

          <VisitasSection
            docente={docenteSeleccionado}
            visitasData={data.visitas}
            etapasActivas={etapasActivas}
          />

          <div
            className="graficos"
            style={{ display: "flex", gap: "2rem", flexDirection: "row" }}
          >
            <GraficosSection
              docente={docenteSeleccionado}
              interaccionData={data.interaccionData}
              progresoData={data.progresoData}
              momento={momento}
              institucionId={institucionSeleccionada?.id}
              etapasActivas={etapasActivas}
            />
          </div>

          <ObservacionesSection docente={docenteSeleccionado} />

          <Autovaloracion
            docente={docenteSeleccionado}
            questions={data.autovaloracion.questions}
            answers={data.autovaloracion.answers}
            momento={momento}
          />

          <Equipo docente={docenteSeleccionado} />
        </>
      ) : (
        <p style={{ textAlign: "center", marginTop: "2rem" }}>
          {modoInstitucion
            ? "Seleccione un docente para ver el reporte."
            : "Seleccione un colegio y un docente para ver el reporte."}
        </p>
      )}
    </div>
  );
};

export default ReporteDocente;